// ======================================
// How It Works Page Module
// ======================================

import { icons } from '../components.js';

const processSteps = [
    {
        number: '01',
        title: 'Free Consultation',
        description: 'Share your vision with our design experts. We understand your lifestyle, preferences, and budget to craft the perfect plan for your home.',
        duration: 'Day 1',
        icon: icons.messageCircle,
        details: ['Understand your style preferences', 'Space assessment', 'Budget discussion', 'Timeline planning']
    },
    {
        number: '02',
        title: 'On-Site Measurement',
        description: 'Our team visits your home for precise measurements. Every detail matters — from electrical points to plumbing lines — ensuring a flawless fit.',
        duration: 'Day 2-3',
        icon: icons.tool,
        details: ['Precise room measurements', 'Electrical & plumbing mapping', 'Structural assessment', 'Photo documentation']
    },
    {
        number: '03',
        title: '3D Design & Approval',
        description: 'Experience your dream home before it\'s built. Our designers create photorealistic 3D renders so you can visualize every detail.',
        duration: 'Week 1-2',
        icon: icons.eye,
        details: ['Multiple design concepts', 'Photorealistic 3D renders', 'Virtual walkthrough', 'Unlimited revisions']
    },
    {
        number: '04',
        title: 'Material Finalization',
        description: 'Choose from our curated collection of premium materials. Visit our experience center to see and feel the finishes that will transform your space.',
        duration: 'Week 2-3',
        icon: icons.packages,
        details: ['Premium material selection', 'Color & finish choices', 'Hardware selection', 'Material quality assurance']
    },
    {
        number: '05',
        title: 'Production',
        description: 'Your custom pieces are manufactured with precision in our state-of-the-art facility. Quality checks at every stage ensure perfection.',
        duration: 'Week 3-6',
        icon: icons.tool,
        details: ['Factory manufacturing', 'Quality control checks', 'Weather-resistant materials', 'Modular precision cutting']
    },
    {
        number: '06',
        title: 'Installation & Handover',
        description: 'Our expert installation team brings your design to life. Clean, efficient, and supervised — we don\'t leave until you\'re delighted.',
        duration: 'Week 6-8',
        icon: icons.home,
        details: ['Professional installation', 'Site supervision', 'Cleanup & finishing', 'Quality walkthrough']
    },
    {
        number: '07',
        title: 'Warranty & Support',
        description: 'Your satisfaction is our commitment. Enjoy peace of mind with our comprehensive 10-year warranty and dedicated after-sales support.',
        duration: 'Ongoing',
        icon: icons.shield,
        details: ['10-year warranty', 'Free service visits', 'Dedicated support line', 'Annual maintenance check']
    }
];

const guarantees = [
    { icon: icons.shield, title: '10-Year Warranty', desc: 'Comprehensive coverage on all modular installations' },
    { icon: icons.calendar, title: '45-Day Delivery', desc: 'Guaranteed on-time project completion' },
    { icon: icons.check, title: 'No Hidden Costs', desc: 'Transparent pricing with no surprise charges' },
    { icon: icons.award, title: 'Quality Assured', desc: 'ISO-certified manufacturing processes' }
];

export function renderHowItWorksPage() {
    return `
    <section class="page-header" style="background: linear-gradient(135deg, var(--color-primary), var(--color-secondary)); color: white;">
      <div class="container">
        <div class="page-header-content" style="text-align:center;">
          <p style="color: var(--color-accent-light); font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: var(--space-4);">Our Process</p>
          <h1 class="page-title" style="color:white;">How It Works</h1>
          <p class="page-subtitle" style="color: rgba(255,255,255,0.85); max-width: 640px; margin: 0 auto;">
            From your first call to the final walkthrough — here's how we create stunning interiors in 7 simple steps.
          </p>
        </div>
      </div>
    </section>

    <!-- Process Steps -->
    <section class="section" style="padding: var(--space-20) 0;">
      <div class="container">
        <div class="process-timeline">
          ${processSteps.map((step, i) => `
            <div class="process-step animate-fade-in-up delay-${Math.min(i + 1, 5)}" style="display: grid; grid-template-columns: 80px 1fr; gap: var(--space-8); margin-bottom: var(--space-12); position: relative;">
              <div style="text-align: center;">
                <div style="width: 64px; height: 64px; border-radius: 50%; background: ${i % 2 === 0 ? 'var(--color-primary)' : 'var(--color-accent)'}; color: white; display: flex; align-items: center; justify-content: center; font-family: var(--font-heading); font-size: var(--text-2xl); font-weight: var(--font-bold); margin: 0 auto;">
                  ${step.number}
                </div>
                ${i < processSteps.length - 1 ? '<div style="width: 2px; height: 100%; background: var(--color-border); margin: var(--space-4) auto 0;"></div>' : ''}
              </div>
              <div style="padding-bottom: var(--space-8);">
                <p style="color: var(--color-accent-dark); font-weight: 600; font-size: var(--text-sm); letter-spacing: 0.05em; margin-bottom: var(--space-2);">${step.duration}</p>
                <h3 style="font-family: var(--font-heading); font-size: var(--text-2xl); margin-bottom: var(--space-3);">${step.title}</h3>
                <p style="color: var(--color-text-muted); line-height: 1.7; margin-bottom: var(--space-6);">${step.description}</p>
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-3);">
                  ${step.details.map(d => `
                    <div style="display: flex; align-items: center; gap: var(--space-2); color: var(--color-text-secondary); font-size: var(--text-sm);">
                      <span style="color: var(--color-primary); flex-shrink: 0;">${icons.check}</span>
                      ${d}
                    </div>
                  `).join('')}
                </div>
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    </section>

    <!-- Guarantees -->
    <section class="section" style="background: var(--color-primary-50); padding: var(--space-16) 0;">
      <div class="container">
        <h2 style="font-family: var(--font-heading); font-size: var(--text-3xl); text-align: center; margin-bottom: var(--space-12);">Our Guarantees</h2>
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: var(--space-8);">
          ${guarantees.map(g => `
            <div style="background: white; border-radius: var(--radius-xl); padding: var(--space-8); text-align: center; box-shadow: var(--shadow-md); transition: transform 0.3s ease, box-shadow 0.3s ease;" onmouseover="this.style.transform='translateY(-4px)';this.style.boxShadow='var(--shadow-lg)'" onmouseout="this.style.transform='';this.style.boxShadow='var(--shadow-md)'">
              <div style="width: 56px; height: 56px; border-radius: 50%; background: var(--color-primary-50); color: var(--color-primary); display: flex; align-items: center; justify-content: center; margin: 0 auto var(--space-4);">
                ${g.icon}
              </div>
              <h3 style="font-family: var(--font-heading); font-size: var(--text-xl); margin-bottom: var(--space-2);">${g.title}</h3>
              <p style="color: var(--color-text-muted); font-size: var(--text-sm);">${g.desc}</p>
            </div>
          `).join('')}
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="section" style="padding: var(--space-20) 0; text-align: center;">
      <div class="container">
        <h2 style="font-family: var(--font-heading); font-size: var(--text-4xl); margin-bottom: var(--space-4);">Ready to Transform Your Home?</h2>
        <p style="color: var(--color-text-muted); max-width: 500px; margin: 0 auto var(--space-8);">
          Start your journey with a free design consultation. No obligations, just expert advice.
        </p>
        <div style="display: flex; gap: var(--space-4); justify-content: center; flex-wrap: wrap;">
          <button class="btn btn-primary btn-lg" data-open-modal="lead-modal">Book Free Consultation</button>
          <a href="/estimator" class="btn btn-secondary btn-lg">Get Cost Estimate</a>
        </div>
      </div>
    </section>
  `;
}

export function initHowItWorksPage() {
    // No specific initialization needed
}
